import { useState } from 'react';
import { rateLimiter, RATE_LIMITS } from '@/lib/rateLimiter';
import { analytics } from '@/lib/analytics';
import { walletTopUpSchema } from '@/lib/validationSchemas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Coins, CreditCard, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TopUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const COIN_PACKAGES = [
  { coins: 50, price: 0.50, label: 'Vote Pack', description: '~5-10 votes', popular: false },
  { coins: 100, price: 1, label: 'Starter', description: '10-20 votes', popular: false },
  { coins: 250, price: 2.5, label: 'Tip Pack', description: '2-5 tips', popular: true },
  { coins: 500, price: 5, label: 'Supporter', description: '5-10 tips', popular: false },
  { coins: 1000, price: 10, label: 'Super Fan', description: 'Unlock content', popular: false }
];

export function TopUpModal({ open, onOpenChange, onSuccess }: TopUpModalProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleQuickSelect = (coins: number) => {
    setSelectedAmount(coins);
    setCustomAmount(coins.toString());
  };

  const handleCustomInput = (value: string) => {
    setCustomAmount(value);
    const parsed = parseInt(value);
    if (!isNaN(parsed) && parsed > 0) {
      setSelectedAmount(parsed);
    } else {
      setSelectedAmount(null);
    }
  };

  const calculatePrice = (coins: number) => {
    return (coins / 100).toFixed(2);
  };

  const handleTopUp = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to continue');
        return;
      }

      // Convert coins to naira (100 coins = $1, $1 = ₦1000)
      const usdAmount = selectedAmount / 100;
      const nairaAmount = usdAmount * 1000;

      // Validate minimum purchase
      const validation = walletTopUpSchema.safeParse({
        amount: nairaAmount,
        coins: selectedAmount,
      });

      if (!validation.success) {
        toast.error(validation.error.errors[0].message);
        setIsProcessing(false);
        return;
      }

      // Rate limit check
      const rateLimitKey = `wallet_topup_${user.id}`;
      if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.WALLET_TOPUP.limit, RATE_LIMITS.WALLET_TOPUP.windowMs)) {
        toast.error('Too many top-up attempts. Please wait a few minutes.');
        setIsProcessing(false);
        return;
      }

      console.log('Initiating payment:', { selectedAmount, usdAmount, nairaAmount });

      const { data, error } = await supabase.functions.invoke('flutterwave-initialize', {
        body: {
          amount: nairaAmount,
          email: user.email,
          coins: selectedAmount
        },
      });

      if (error) {
        console.error('Payment initialization error:', error);
        throw error;
      }

      if (!data?.payment_link || !data?.tx_ref) {
        throw new Error('Invalid payment response');
      }

      // Store reference for verification after redirect
      sessionStorage.setItem('flutterwave_tx_ref', data.tx_ref);
      sessionStorage.setItem('flutterwave_coins', selectedAmount.toString());
      
      // Track analytics
      analytics.walletTopUp(nairaAmount, selectedAmount);
      
      // Redirect to Flutterwave payment page
      window.location.href = data.payment_link;
    } catch (error) {
      console.error('Top-up error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-yellow-500" />
            Top Up Wallet
          </DialogTitle>
          <DialogDescription>
            Purchase StageCoins to tip creators and vote on polls
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Quick Select Packages */}
          <div>
            <label className="text-sm font-medium mb-3 block">
              Popular Packages
            </label>
            <div className="grid grid-cols-2 gap-3">
              {COIN_PACKAGES.map((pkg) => (
                <Card 
                  key={pkg.coins}
                  className={`cursor-pointer transition-all hover:shadow-md touch-manipulation ${
                    selectedAmount === pkg.coins 
                      ? 'border-primary ring-2 ring-primary bg-primary/5' 
                      : 'hover:border-primary/50'
                  } ${pkg.popular ? 'relative' : ''}`}
                  onClick={() => handleQuickSelect(pkg.coins)}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-semibold shadow-sm">
                      Popular
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Coins className="h-4 w-4 text-yellow-500" />
                      <p className="text-xl font-bold">{pkg.coins}</p>
                    </div>
                    <p className="text-xs font-medium text-foreground mb-1">{pkg.label}</p>
                    <p className="text-xs text-muted-foreground mb-2">{pkg.description}</p>
                    <p className="text-sm font-semibold text-primary">${pkg.price.toFixed(2)}</p>
                    {selectedAmount === pkg.coins && (
                      <CheckCircle2 className="h-4 w-4 text-primary mt-1" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Custom Amount
            </label>
            <div className="space-y-2">
              <div className="relative">
                <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="Enter coins amount (min: 100)"
                  value={customAmount}
                  onChange={(e) => handleCustomInput(e.target.value)}
                  className="pl-10"
                  min="100"
                />
              </div>
              {selectedAmount && selectedAmount > 0 && (
                <p className="text-sm text-muted-foreground">
                  = ${calculatePrice(selectedAmount)} USD (≈ ₦{(parseFloat(calculatePrice(selectedAmount)) * 1000).toFixed(2)})
                </p>
              )}
            </div>
          </div>

          {/* Exchange Rate & Limits Info */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium mb-1">Exchange Rate</p>
            <p className="text-muted-foreground">100 StageCoins = $1.00 USD = ₦1,000</p>
            <p className="text-muted-foreground">• Minimum purchase: 100 coins (₦1,000)</p>
          </div>

          {/* Payment Button */}
          <Button 
            onClick={handleTopUp} 
            disabled={!selectedAmount || isProcessing}
            className="w-full h-12 text-base"
            size="lg"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Pay ${selectedAmount ? calculatePrice(selectedAmount) : '0.00'}
              </>
            )}
          </Button>

          {/* Security Notice */}
          <p className="text-xs text-center text-muted-foreground">
            Secure payment powered by Flutterwave. Your payment information is encrypted and secure.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
